import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { OWNER_EMAIL } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMELINES = ["ASAP", "1–3 months", "3–6 months", "Flexible"];

export const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", timeline: "", message: "" });
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const pill = (key, value) => setForm((f) => ({ ...f, [key]: f[key] === value ? "" : value }));

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Enquiry sent — we'll get back to you within one business day.");
            setForm({ name: "", email: "", phone: "", company: "", timeline: "", message: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Couldn't send your enquiry. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" className="relative py-20 lg:py-32 overflow-hidden">
            <div className="absolute -top-32 right-0 w-[420px] h-[420px] rounded-full bg-violet-300/25 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-pink-300/20 blur-[120px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
                <motion.div
                    initial={{ opacity: 0, x: -40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-5"
                >
                    <div className="font-mono-brand text-xs tracking-[0.25em] uppercase font-semibold text-violet-600 mb-4" data-testid="contact-eyebrow">
                        Contact us
                    </div>
                    <h2 className="font-display font-extrabold tracking-tight text-slate-900 text-2xl sm:text-3xl lg:text-4xl leading-snug">
                        Have an idea? <span className="gradient-text">Let's paint it real.</span>
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
                        Tell us about your project and we'll reply within one business day with next steps — no obligation, no jargon.
                    </p>
                    <div className="mt-9 space-y-5">
                        <div className="flex items-center gap-4" data-testid="contact-info-email">
                            <div className="h-11 w-11 rounded-xl gradient-bg flex items-center justify-center shadow-lg shadow-violet-500/20">
                                <Mail size={18} className="text-white" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Email us</div>
                                <a href={`mailto:${OWNER_EMAIL}`} className="text-sm font-semibold text-slate-900 hover:text-violet-600 transition-colors">{OWNER_EMAIL}</a>
                            </div>
                        </div>
                        <div className="flex items-center gap-4" data-testid="contact-info-location">
                            <div className="h-11 w-11 rounded-xl gradient-bg flex items-center justify-center shadow-lg shadow-pink-500/20">
                                <MapPin size={18} className="text-white" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Where we work</div>
                                <div className="text-sm font-semibold text-slate-900">Regional & international, fully remote</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4" data-testid="contact-info-response">
                            <div className="h-11 w-11 rounded-xl gradient-bg flex items-center justify-center shadow-lg shadow-amber-500/20">
                                <Clock size={18} className="text-white" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Response time</div>
                                <div className="text-sm font-semibold text-slate-900">Within one business day</div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-7"
                >
                    <form
                        data-testid="contact-form"
                        onSubmit={submit}
                        className="rounded-[1.75rem] border border-white/60 bg-white/80 backdrop-blur-xl p-7 sm:p-10 shadow-2xl shadow-violet-500/10 space-y-6"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                                <label htmlFor="contact-name" className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Name *</label>
                                <input
                                    id="contact-name"
                                    data-testid="contact-name-input"
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="Your full name"
                                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition"
                                />
                            </div>
                            <div>
                                <label htmlFor="contact-email" className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email *</label>
                                <input
                                    id="contact-email"
                                    data-testid="contact-email-input"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="you@company.com"
                                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                                <label htmlFor="contact-phone" className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact number</label>
                                <input
                                    id="contact-phone"
                                    data-testid="contact-phone-input"
                                    type="tel"
                                    value={form.phone}
                                    onChange={set("phone")}
                                    placeholder="+91 98765 43210"
                                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition"
                                />
                            </div>
                            <div>
                                <label htmlFor="contact-company" className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</label>
                                <input
                                    id="contact-company"
                                    data-testid="contact-company-input"
                                    value={form.company}
                                    onChange={set("company")}
                                    placeholder="Company or project name (optional)"
                                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition"
                                />
                            </div>
                        </div>

                        <div>
                            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Timeline</div>
                            <div className="mt-2.5 flex flex-wrap gap-2" data-testid="contact-timeline-pills">
                                {TIMELINES.map((t) => (
                                    <button
                                        type="button"
                                        key={t}
                                        data-testid={`contact-timeline-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                        onClick={() => pill("timeline", t)}
                                        className={`text-xs font-medium rounded-full px-4 py-2 border transition-all duration-300 ${
                                            form.timeline === t
                                                ? "gradient-bg text-white border-transparent shadow-md shadow-pink-500/25"
                                                : "border-slate-200 text-slate-600 bg-white/80 hover:border-pink-300 hover:text-pink-600"
                                        }`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label htmlFor="contact-message" className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Project details *</label>
                            <textarea
                                id="contact-message"
                                data-testid="contact-message-input"
                                required
                                minLength={10}
                                rows={4}
                                value={form.message}
                                onChange={set("message")}
                                placeholder="What are you building? What does success look like?"
                                className="mt-2 w-full rounded-xl border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            data-testid="contact-submit-button"
                            disabled={sending}
                            className="gradient-bg text-white font-semibold px-8 py-3.5 rounded-full inline-flex items-center gap-2.5 shadow-xl shadow-violet-500/25 hover:shadow-pink-500/35 hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                        >
                            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            {sending ? "Sending…" : "Send Project Enquiry"}
                        </button>
                    </form>
                </motion.div>
            </div>
        </section>
    );
};
